import { Config, PixelStreaming, Messages, BaseMessage } from "@epicgames-ps/lib-pixelstreamingfrontend-ue5.7";
import { DIGITAL_TWIN_TIMEOUT, getDigitalTwinSettings } from "./digital-twin-config";

export interface ReachableResult {
    resolved: boolean;
    error?: string;
}

export const URL_TIMEOUT = 5000;

/**
 * Fetches the given URL and return resolved as true if the response is ok and false otherwise.
 * If the fetch fails or times out, it returns resolved as false and includes the error message.
 * @param url The URL to test
 * @param timeout Time in milliseconds to wait before failing due to timeout, default is 5000ms
 * @returns ReachableResult containing whether the URL resolves and any error message if it doesn't
 */
export const testIfUrlIsReachable = async (url: string, timeout = URL_TIMEOUT): Promise<ReachableResult> => {
    try {
        const controller = new AbortController();
        const { signal } = controller;
        const fetchTimeout = setTimeout(() => {
            controller.abort();
        }, timeout);
        const response = await fetch(url, { method: "HEAD", signal });
        clearTimeout(fetchTimeout);
        return { resolved: response.ok };
    } catch (error) {
        return { resolved: false, error: (error as Error).message };
    }
};

export const WEBSOCKET_TIMEOUT = 10000;

/**
 * Tests if a WebSocket server is reachable by attempting to open a connection.
 * Fails if onClose is called with a code other than 1000, or onClose is called before onOpen
 * @param wsUrl The WebSocket URL to test
 * @param signal Abort Signal which will close the websocket connection
 * @param timeout Time in milliseconds to wait before failing due to timeout, default is 10000ms
 * @returns ReachableResult containing whether the WebSocket server is reachable and any error message if it isn't
 */
export const testIfWSServerIsReachable = (wsUrl: string, signal?: AbortSignal, timeout = WEBSOCKET_TIMEOUT): Promise<ReachableResult> =>
    new Promise((resolve) => {
        let isResolved = false;
        let ws: WebSocket;
        const websocketsTimeoutCheck = setTimeout(() => {
            if (!isResolved) {
                isResolved = true;
                ws.close();
            }
            resolve({ resolved: false, error: "WebSocket timed out" });
        }, timeout);

        if (signal) {
            signal.addEventListener("abort", () => {
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(websocketsTimeoutCheck);
                    ws?.close();
                    resolve({ resolved: false, error: "WebSocket aborted" });
                }
            });
        }

        // First test if it is reachable
        try {
            ws = new WebSocket(wsUrl);
        } catch (error) {
            clearTimeout(websocketsTimeoutCheck);
            resolve({ resolved: false, error: (error as Error).message });
            return;
        }

        ws.onopen = () => {
            if (!isResolved) {
                isResolved = true;
                clearTimeout(websocketsTimeoutCheck);
                ws.close();
            }
            resolve({ resolved: true });
        };

        ws.onerror = (ev) => {
            // We don't want to resolve onerror as these can trigger without being fatal
            // They also provide no info on the error itself

            // eslint-disable-next-line no-console
            console.error("WebSocket error:", ev);
        };

        ws.onclose = (ev) => {
            if (!isResolved && ev.code !== 1000) {
                isResolved = true;
                clearTimeout(websocketsTimeoutCheck);
                resolve({ resolved: false, error: `WebSocket closed: ${ev.code} ${ev.reason}` });
            }
        };
    });

// Object used to store all the data related to the WebRTC connection for debugging purposes
// This is sent to sentry on error
interface WebRTCDebugConnectionData {
    localIceCandidates: string[];
    remoteIceCandidates: string[];
    configString: string;
    connectionStateChanges: string[];
    signalingStateChanges: string[];
    iceConnectionStateChanges: string[];
    messages: (BaseMessage | string)[];
}

export interface PixelStreamingConnectionTestResult extends ReachableResult {
    connectionData?: WebRTCDebugConnectionData;
}

/**
 * Tests if a connection can be established to a Pixel Streaming server at the given URL. It attempts to connect and waits for either a successful stream start or a failure event. It collects all relevant messages and events during the connection process for debugging purposes.
 * @param url The URL of the Pixel Streaming server to test
 * @param signal Abort Signal which will close the pixel streaming connection
 * @param timeout Time in milliseconds to wait before failing due to timeout, default is DIGITAL_TWIN_TIMEOUT
 * @returns PixelStreamingConnectionTestResult containing whether the connection was successful, any error message if it wasn't, and debug data about the connection process
 */
export const testPixelStreamingConnection = async (url: string, signal?: AbortSignal, timeout = DIGITAL_TWIN_TIMEOUT): Promise<PixelStreamingConnectionTestResult> =>
    new Promise((resolvePromise) => {
        const connectionData: WebRTCDebugConnectionData = {
            localIceCandidates: [],
            remoteIceCandidates: [],
            configString: "",
            connectionStateChanges: [],
            signalingStateChanges: [],
            iceConnectionStateChanges: [],
            messages: [],
        };

        let pixelStreaming: PixelStreaming;

        let isResolved = false;

        const pixelStreamingTimeoutCheck = setTimeout(() => {
            if (!isResolved) {
                connectionData.messages.push("WebRTC connection timed out.");
                resolve(false, "WebRTC connection timed out.");
            }
        }, timeout);

        if (signal) {
            signal.addEventListener("abort", () => {
                if (!isResolved) {
                    connectionData.messages.push("WebRTC connection aborted.");
                    resolve(false, "WebRTC connection aborted.");
                }
            });
        }

        const resolve = (resolveValue: boolean, error?: string) => {
            if (!isResolved) {
                isResolved = true;
                clearTimeout(pixelStreamingTimeoutCheck);
                resolvePromise({ resolved: resolveValue, error, connectionData });
            }
            pixelStreaming?.signallingProtocol?.sendMessage({ type: "unsubscribe" });
            pixelStreaming?.disconnect();
        };

        const config = new Config({
            initialSettings: getDigitalTwinSettings(url),
        });
        pixelStreaming = new PixelStreaming(config);

        pixelStreaming.signallingProtocol.addListener(Messages.iceCandidate.typeName, (msg: BaseMessage) => {
            const iceCandidateMessage = msg as Messages.iceCandidate;
            connectionData.remoteIceCandidates.push(iceCandidateMessage.candidate.candidate);
            connectionData.messages.push(msg);
        });

        pixelStreaming.signallingProtocol.addListener(Messages.config.typeName, (msg: BaseMessage) => {
            connectionData.configString = JSON.stringify((msg as Messages.config).peerConnectionOptions);
            connectionData.messages.push(msg);
        });

        pixelStreaming.signallingProtocol.addListener(Messages.streamerList.typeName, (msg: BaseMessage) => {
            connectionData.messages.push(msg);
        });

        pixelStreaming.signallingProtocol.addListener(Messages.subscribeFailed.typeName, (msg: BaseMessage) => {
            connectionData.messages.push(msg);
        });

        pixelStreaming.signallingProtocol.addListener(Messages.answer.typeName, (msg: BaseMessage) => {
            connectionData.messages.push(msg);
        });
        pixelStreaming.signallingProtocol.addListener(Messages.offer.typeName, (msg: BaseMessage) => {
            connectionData.messages.push(msg);
        });
        pixelStreaming.signallingProtocol.addListener(Messages.peerDataChannels.typeName, (msg: BaseMessage) => {
            connectionData.messages.push(msg);
        });

        // This is called on websocket open
        pixelStreaming.signallingProtocol.transport.addListener("open", () => {
            connectionData.messages.push("Transport connection opened.");
        });

        // Websocket error
        pixelStreaming.signallingProtocol.transport.addListener("error", () => {
            connectionData.messages.push("Transport connection error.");
        });

        pixelStreaming.signallingProtocol.transport.addListener("out", (msg: BaseMessage) => {
            if (msg.type === Messages.iceCandidate.typeName) {
                connectionData.localIceCandidates.push((msg as Messages.iceCandidate).candidate.candidate);
            }
        });

        pixelStreaming.addEventListener("playStream", () => {
            connectionData.messages.push("Play stream started.");
            resolve(true);
        });

        pixelStreaming.addEventListener("playStreamRejected", () => {
            connectionData.messages.push("Play stream rejected.");
            resolve(false, "Play stream rejected by server.");
        });

        pixelStreaming.addEventListener("subscribeFailed", () => {
            // Subscribe can fail without it being fatal so don't resolve on this
            connectionData.messages.push("Subscribe failed.");
        });

        pixelStreaming.addEventListener("webRtcFailed", () => {
            connectionData.messages.push("WebRTC connection failed.");
            resolve(false, "WebRTC connection failed.");
        });
        pixelStreaming.connect();
    });
